import { useState } from 'react';
import axios from 'axios';
import { Pill, Plus, CheckCircle2, Bell, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { API_URL, MED_FORMS, INJECTION_ROUTES, FREQUENCIES } from './constants';

export function MedicationsTab({ medications, clientId, onRefresh, user }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminDialog, setAdminDialog] = useState(null);
  const [reminderDialog, setReminderDialog] = useState(null);
  const [form, setForm] = useState({ medication_name: '', form: 'tablet', injection_route: '', dose: '', frequency: 'OD', start_date: '', stop_date: '', notes: '' });
  const [reminderForm, setReminderForm] = useState({ reminder_time: '08:00', enabled: true });
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!form.medication_name || !form.start_date) return toast.error('Name and start date required');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/clinical/medications`, { ...form, client_id: clientId }, { withCredentials: true });
      toast.success('Medication added');
      setDialogOpen(false);
      setForm({ medication_name: '', form: 'tablet', injection_route: '', dose: '', frequency: 'OD', start_date: '', stop_date: '', notes: '' });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSubmitting(false);
  };

  const handleAdminister = async (outcome) => {
    if (!adminDialog) return;
    try {
      await axios.post(`${API_URL}/api/clinical/medications/${adminDialog.id}/administer`, { outcome, notes: '' }, { withCredentials: true });
      toast.success(`Medication — ${outcome.replace(/_/g, ' ')}`);
      setAdminDialog(null);
      onRefresh();
    } catch (err) { toast.error('Failed'); }
  };

  const handleSetReminder = async () => {
    if (!reminderDialog) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/clinical/medications/${reminderDialog.id}/reminder`, reminderForm, { withCredentials: true });
      toast.success('Reminder set');
      setReminderDialog(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to set reminder'); }
    setSubmitting(false);
  };

  // Compute next due time based on frequency
  const getNextDue = (med) => {
    const freq = med.frequency;
    const hours = { OD: 24, BD: 12, TDS: 8, QDS: 6 };
    if (freq === 'PRN') return 'As needed';
    const h = hours[freq] || 24;
    if (med.last_administered) {
      const last = new Date(med.last_administered);
      const next = new Date(last.getTime() + h * 60 * 60 * 1000);
      const now = new Date();
      if (next < now) return 'OVERDUE';
      const diff = Math.round((next - now) / (60 * 1000));
      if (diff < 60) return `Due in ${diff}m`;
      return `Due in ${Math.round(diff / 60)}h`;
    }
    return `Every ${h}h`;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={() => setDialogOpen(true)} data-testid="add-medication-btn"><Plus className="w-4 h-4 mr-1.5" />Add Medication</Button>
      </div>

      {medications.length === 0 ? (
        <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Pill className="w-12 h-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No medications recorded</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {medications.map(med => {
            const nextDue = getNextDue(med);
            const isOverdue = nextDue === 'OVERDUE';
            return (
              <Card key={med.id} className={`border ${isOverdue ? 'border-red-500/50 bg-red-500/5' : med.status === 'active' ? 'border-[#00A6A0]/30' : 'border-border opacity-60'}`} data-testid={`med-${med.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-semibold text-foreground">{med.medication_name}</p>
                        {med.reminder_enabled && <Bell className="w-3.5 h-3.5 text-amber-400" title="Reminder active" />}
                        {isOverdue && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400 animate-pulse">OVERDUE</span>}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {MED_FORMS.find(f => f.id === med.form)?.label || med.form}
                        {med.form === 'injection' && med.injection_route && ` (${INJECTION_ROUTES.find(r => r.id === med.injection_route)?.label || med.injection_route})`}
                        {med.dose && ` — ${med.dose}`}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <p className="text-sm text-[#00A6A0] font-medium">{FREQUENCIES.find(f => f.id === med.frequency)?.label || med.frequency}</p>
                        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{nextDue}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Start: {med.start_date} {med.stop_date && `| Review/Stop: ${med.stop_date}`}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Signed: {med.administered_by_name} ({med.staff_number || med.usic}) — {new Date(med.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {med.status === 'active' && (
                        <>
                          <Button size="sm" className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={() => setAdminDialog(med)} data-testid={`administer-${med.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" />Sign MAR
                          </Button>
                          <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-500 text-xs" onClick={() => { setReminderDialog(med); setReminderForm({ reminder_time: med.reminder_time || '08:00', enabled: true }); }} data-testid={`reminder-${med.id}`}>
                            <Bell className="w-3 h-3 mr-1" />Reminder
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Medication Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card border-border sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-foreground">Add Medication</DialogTitle><DialogDescription className="text-muted-foreground">Prescribe medication with dose, form, and frequency</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label className="text-foreground">Medication Name *</Label>
              <Input value={form.medication_name} onChange={e => setForm(p => ({...p, medication_name: e.target.value}))} placeholder="e.g., Paracetamol 500mg" className="mt-1 bg-muted border-border text-foreground" data-testid="med-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-foreground">Form/Mode *</Label>
                <select value={form.form} onChange={e => setForm(p => ({...p, form: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="med-form-select">
                  {MED_FORMS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select></div>
              {form.form === 'injection' && (
                <div><Label className="text-foreground">Injection Route *</Label>
                  <select value={form.injection_route} onChange={e => setForm(p => ({...p, injection_route: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="med-injection-route">
                    <option value="">Select route...</option>
                    {INJECTION_ROUTES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select></div>
              )}
              <div><Label className="text-foreground">Dose</Label>
                <Input value={form.dose} onChange={e => setForm(p => ({...p, dose: e.target.value}))} placeholder="e.g., 500mg, 5ml" className="mt-1 bg-muted border-border text-foreground" data-testid="med-dose-input" /></div>
            </div>
            <div><Label className="text-foreground">Frequency *</Label>
              <select value={form.frequency} onChange={e => setForm(p => ({...p, frequency: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="med-frequency-select">
                {FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-foreground">Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} className="mt-1 bg-muted border-border text-foreground" data-testid="med-start-date" /></div>
              <div><Label className="text-foreground">Stop/Review Date</Label>
                <Input type="date" value={form.stop_date} onChange={e => setForm(p => ({...p, stop_date: e.target.value}))} className="mt-1 bg-muted border-border text-foreground" data-testid="med-stop-date" /></div>
            </div>
            <div><Label className="text-foreground">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" placeholder="Additional notes..." data-testid="med-notes" /></div>
            <Button className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" onClick={handleAdd} disabled={submitting} data-testid="submit-medication-btn">
              {submitting ? 'Adding...' : 'Add & Sign Medication'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign MAR Dialog — with self_administered and by_family options */}
      {adminDialog && (
        <Dialog open={!!adminDialog} onOpenChange={() => setAdminDialog(null)}>
          <DialogContent className="glass-card border-border sm:max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Sign MAR — {adminDialog.medication_name}</DialogTitle><DialogDescription className="text-muted-foreground">Record administration outcome</DialogDescription></DialogHeader>
            <p className="text-sm text-muted-foreground">Signing as: <span className="text-[#00A6A0] font-medium">{user?.name}</span> ({user?.staff_number || user?.usic})</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleAdminister('given')} data-testid="mar-given">Given</Button>
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-white" onClick={() => handleAdminister('self_administered')} data-testid="mar-self">Self Administered</Button>
              <Button className="bg-indigo-500 hover:bg-indigo-600 text-white" onClick={() => handleAdminister('by_family')} data-testid="mar-family">By Family</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => handleAdminister('refused')} data-testid="mar-refused">Refused</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => handleAdminister('withheld')} data-testid="mar-withheld">Withheld</Button>
              <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleAdminister('not_available')} data-testid="mar-na">Not Available</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reminder Dialog */}
      {reminderDialog && (
        <Dialog open={!!reminderDialog} onOpenChange={() => setReminderDialog(null)}>
          <DialogContent className="glass-card border-border sm:max-w-xs">
            <DialogHeader><DialogTitle className="text-foreground">Set Reminder</DialogTitle><DialogDescription className="text-muted-foreground">{reminderDialog.medication_name}</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-foreground">Reminder Time</Label>
                <Input type="time" value={reminderForm.reminder_time} onChange={e => setReminderForm(p => ({...p, reminder_time: e.target.value}))} className="mt-1 bg-muted border-border text-foreground" data-testid="reminder-time-input" /></div>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={reminderForm.enabled} onChange={e => setReminderForm(p => ({...p, enabled: e.target.checked}))} className="rounded" />
                Enable notification reminders
              </label>
              <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={handleSetReminder} disabled={submitting} data-testid="submit-reminder-btn">
                {submitting ? 'Saving...' : 'Set Reminder'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
